import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button, Input, Select, Table, Tag, Tooltip, Modal } from "antd";
import { Settings2, Link, Trash2, AlignHorizontalDistributeCenter } from "lucide-react";
import { useSelector } from "react-redux";

import {
	createSignal,
	updateSignal,
	listSignals,
	getSignalWebhookUrl,
	createSignalWebhook,
	listStrategies,
	updateStrategyBrokers,
	activateSignal,
	deactivateSignal,
	deleteSignal,
	triggerSignal,
	getAllSignalLogs,
} from "../../../../api/signalApi";
import { getBrokers } from "../../../../api/authentication";
import PlusMinQty from "../../../utilities/PlusMinQty";
import "../QuiktradeAlgo.css";
import "./index.css";
import { getSignalTypeLabel, signalTypes, singleLegSignalTypes, multiLegSignalTypes } from "../utils";
import SubscriptionCheckModal from "components/common/SubscriptionCheckModal";
import { useNavigate } from "react-router-dom";
import { useToast } from "components/common/custom-toast/Toast";
import SignalDashboard from "./SignalDashboard";
import CumulativeSignalLogs from "./CumulativeSignalLogs";

const { Option } = Select;
const { confirm } = Modal;


const SignalsPage = () => {
	const isLoggedIn = useSelector((s) => s?.user?.isLoggedIn === true);
	const brokerLoggedIn = useSelector((s) => s?.user?.brokerLoggedIn === true);
	const brokerOptions = useSelector((s) => s?.brokers?.list || []);
	const [brokers, setBrokers] = useState([]);

	const [signalName, setSignalName] = useState("");
	const [currentSignalId, setCurrentSignalId] = useState("");
	const [strategies, setStrategies] = useState([]);
	const [strategyRows, setStrategyRows] = useState([]);
	const [selectKey, setSelectKey] = useState(0);
	const [signals, setSignals] = useState([]);
	const [loading, setLoading] = useState(false);
	const [strategySelectValue, setStrategySelectValue] = useState(undefined);
	const [allSignalLogs, setAllSignalLogs] = useState([]);
	const [allLogsLoading, setAllLogsLoading] = useState(false);
	const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
	const accessiblePlans = useSelector((state) => state?.packages?.accessiblePlans);
	const plansLoading = accessiblePlans === undefined;
	const hasAlgoAccess = accessiblePlans?.length > 0 ? accessiblePlans?.some(planName =>
		planName === 'QT-Unlimited' || planName === 'QT-Algo' || planName === 'trial'
	) : false;
	const navigate = useNavigate()
	const toast = useToast()
	const brokersList = useSelector((state) => state?.brokers?.list || []);

	const fetchAllSignalLogs = useCallback(async () => {
		setAllLogsLoading(true);
		try {
			const logsRes = await getAllSignalLogs();
			if (logsRes?.code === 200 && Array.isArray(logsRes?.data)) {
				setAllSignalLogs(logsRes.data);
			} else {
				setAllSignalLogs([]);
			}
		} catch (e) {
			setAllSignalLogs([]);
		} finally {
			setAllLogsLoading(false);
		}
	}, []);

	const getBrokerLabel = (id) => {
		const b = (brokerOptions || []).find(x => (x?.id?.toString?.() || '') === (id?.toString?.() || ''));
		if (!b) return id;
		return b?.name?.toUpperCase() || `${b?.type?.toUpperCase?.()} - ${b?.userName?.toUpperCase?.()}` || id;
	};


	const isBrokersLoading = useSelector((state) => state?.brokers?.isLoading);
	const isPlansLoading = useSelector((state) => state?.packages?.isLoading);

	useEffect(() => {
		if (isBrokersLoading || isPlansLoading) {
			return;
		}

		if (!isLoggedIn) {
			toast.error("Please login first");
			navigate("/authentication/login");
			return;
		}

		if (hasAlgoAccess === false) {
			setShowSubscriptionModal(true);
			return;
		}
		if (!brokerLoggedIn) {
			toast.error("Please connect your broker account");
			navigate("/broker-login");
			return;
		}
		const purposed = (brokersList || []).filter(b => (b.purpose === 'algo' || b.type === 'papertrading'));
		if (purposed.length === 0) {
			toast.error("Please use broker account with purpose 'algo' to access Algo signals");
			navigate("/broker-login");
			return;
		}

		(async () => {
			const res = await listStrategies();
			if (res?.code === 200 && Array.isArray(res?.data)) {
				setStrategies(res.data);
			}
			const sig = await listSignals();
			if (sig?.code === 200 && Array.isArray(sig?.data)) {
				setSignals(sig.data);
			}
			try {
				const br = await getBrokers("loggedIn");
				const list = br?.data?.brokers?.filter((b) => b?.quickTrade && (b?.purpose === 'algo' || b?.type === 'papertrading')) || [];
				setBrokers(list);
			} catch (e) {
				setBrokers([]);
			}
			await fetchAllSignalLogs();
		})();
	}, [isLoggedIn, brokerLoggedIn, hasAlgoAccess, isPlansLoading, isBrokersLoading]);

	useEffect(() => {
		const intervalId = setInterval(() => {
			fetchAllSignalLogs();
		}, 5000);

		return () => {
			clearInterval(intervalId);
		};
	}, [fetchAllSignalLogs]);

	const availableStrategies = useMemo(() => strategies.map(s => ({ value: s.id, label: s.name })), [strategies]);



	const removeStrategyRow = (strategyId) => {
		setStrategyRows(prev => prev.filter(r => r.strategyId !== strategyId));
	};

	const updateMultiplier = (strategyId, brokerId, newMultiplier) => {
		setStrategyRows(prev => prev.map(r => {
			if (r.strategyId === strategyId) {
				return {
					...r,
					broker: r.broker.map(b =>
						b.id === brokerId ? { ...b, multiplier: parseFloat(newMultiplier) || 1 } : b
					)
				};
			}
			return r;
		}));
	};

	const addStrategyRow = (strategyId) => {
		if (!strategyId) return;
		const exists = strategyRows.some(r => r.strategyId === strategyId);
		if (exists) return;
		const strategy = strategies.find(s => s.id === strategyId);
		const isMultiLeg = strategy?.singleOrMultiLeg === false;
		const defaultSignalTypes = isMultiLeg ? ['E'] : ['LE'];

		// Extract existing brokers linked to this strategy
		const rawBrokers = Array.isArray(strategy?.brokers) ? strategy.brokers : (strategy?.brokers ? [strategy?.brokers] : []);
		const initialBrokers = rawBrokers.map(b => {
			if (typeof b === 'object' && b?.id) {
				return { id: b.id, multiplier: b.multiplier || 1 };
			} else {
				return { id: b, multiplier: 1 };
			}
		});

		setStrategyRows(prev => ([...prev, { strategyId, signalTypes: defaultSignalTypes, multiLeg: isMultiLeg, broker: initialBrokers }]));
	};

	const saveNameAndGetWebhook = async () => {
		if (!signalName.trim()) {
			toast.error('Enter signal name');
			return;
		}
		setLoading(true);
		try {
			const res = await createSignal({ name: signalName.trim() });
			if (res?.code === 200 && res?.data?.id) {
				const id = res.data.id;
				setCurrentSignalId(id);
				toast.success('Signal created, Please Link Strategies and Broker Accounts');
				const sig = await listSignals();
				if (sig?.code === 200 && Array.isArray(sig?.data)) setSignals(sig.data);
			} else {
				toast.error(res?.error || 'Failed to create signal');
			}
		} finally {
			setLoading(false);
		}
	};

	const saveStrategyLinks = async () => {
		if (!currentSignalId) {
			toast.error('Create and save a signal name first');
			return;
		}
		setLoading(true);
		try {
			// Create separate strategy links for each signal type
			const strategyLinks = [];
			strategyRows.forEach(row => {
				row.signalTypes.forEach(signalType => {
					strategyLinks.push({ strategyId: row.strategyId, signalType });
				});
			});

			const res = await updateSignal(currentSignalId, { name: signalName, strategyLinks });
			if (res?.code === 200) {
				let brokerUpdateFailed = false;
				for (const row of strategyRows) {
					if (row?.broker && row.broker.length > 0) {
						const brokerRes = await updateStrategyBrokers(row.strategyId, { brokers: row.broker });
						if (brokerRes?.code !== 200) {
							brokerUpdateFailed = true;
						}
					}
				}

				if (brokerUpdateFailed) {
					toast.warning('Signal saved, but failed to update linked brokers for some strategies');
				} else {
					toast.success('Signal links saved');
				}

				// Reset form after successful save
				setSignalName("");
				setCurrentSignalId("");
				setStrategyRows([]);
				setSelectKey(k => k + 1);

				const sig = await listSignals();
				if (sig?.code === 200 && Array.isArray(sig?.data)) setSignals(sig.data);

				const strat = await listStrategies();
				if (strat?.code === 200 && Array.isArray(strat?.data)) {
					setStrategies(strat.data);
				}
			} else {
				toast.error(res?.error || 'Failed to save');
			}
		} finally {
			setLoading(false);
		}
	};

	const handleActivate = async (signalId) => {
		setLoading(true);
		try {
			const res = await activateSignal(signalId);
			if (res?.code === 200) {
				toast.success('Signal activated');
				const sig = await listSignals();
				if (sig?.code === 200 && Array.isArray(sig?.data)) setSignals(sig.data);
			}
		} finally { setLoading(false); }
	};

	const handleDeactivate = async (signalId) => {
		setLoading(true);
		try {
			const res = await deactivateSignal(signalId);
			if (res?.code === 200) {
				toast.success('Signal deactivated');
				const sig = await listSignals();
				if (sig?.code === 200 && Array.isArray(sig?.data)) setSignals(sig.data);
			}
		} finally { setLoading(false); }
	};

	const handleDelete = async (signalId) => {
		setLoading(true);
		try {
			const res = await deleteSignal(signalId);
			if (res?.code === 200) {
				toast.success('Signal deleted');
				const sig = await listSignals();
				if (sig?.code === 200 && Array.isArray(sig?.data)) setSignals(sig.data);
			} else if (res?.sessionExpired) {
				toast.error('Session expired. Please login again.');
			} else {
				toast.error(res?.error || 'Failed to delete');
			}
		} finally { setLoading(false); }
	};

	const handleDeleteConfirm = (record) => {
		confirm({
			title: 'Delete Signal',
			icon: null,
			content: `Are you sure you want to delete the signal "${record.signalName || record.name || ''}"? This action cannot be undone.`,
			okText: 'Yes, Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				await handleDelete(record.signalId || record.id);
			},
		});
	};

	const handleBrokerRemoveConfirm = (strategyId, brokerIdToRemove, currentBrokers, onConfirm, onCancel) => {
		const brokerToRemove = (currentBrokers || []).find(b => b.id === brokerIdToRemove);
		if (!brokerToRemove) {
			onConfirm();
			return;
		}

		const strategyName = strategies.find(s => s.id === strategyId)?.name || strategyId;
		const brokerLabel = getBrokerLabel(brokerIdToRemove);

		confirm({
			title: 'Remove Broker Account',
			icon: null,
			content: `If you remove ${brokerLabel} from the strategy "${strategyName}", any existing positions and risk management settings will be ignored by the system. Are you sure you want to continue?`,
			okText: 'Yes, Remove',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: () => {
				onConfirm();
			},
			onCancel: () => {
				if (onCancel) onCancel();
			},
		});
	};

	const handleManualExecution = async (signalId) => {
		setLoading(true);
		try {
			const res = await triggerSignal(signalId);
			toast.success('Signal triggered successfully');
			setLoading(false);
		} catch (error) {
			toast.error('Failed to trigger signal');
			setLoading(false);
		}
	};

	const handleEdit = async (record) => {
		try {
			const signalId = record?.id;
			setSignalName(record?.name || "");
			setCurrentSignalId(signalId || "");


			const full = (signals || []).find(s => s?.id === signalId);
			const strategyLinks = Array.isArray(full?.strategyLinks) ? full.strategyLinks : [];

			const groupedLinks = strategyLinks.reduce((acc, link) => {
				const strategyId = link.strategyId;
				if (!acc[strategyId]) {
					acc[strategyId] = [];
				}
				acc[strategyId].push(link.signalType);
				return acc;
			}, {});

			const rows = Object.keys(groupedLinks).map((strategyId) => {
				const st = strategies.find(s => s.id === strategyId) || {};
				const isMultiLeg = st?.singleOrMultiLeg === false;
				const rawBrokers = Array.isArray(st?.brokers) ? st.brokers : (st?.brokers ? [st?.brokers] : []);
				const brokers = rawBrokers.map(b => {
					if (typeof b === 'object' && b?.id) {
						return { id: b.id, multiplier: b.multiplier || 1 };
					} else {
						return { id: b, multiplier: 1 };
					}
				});
				let signalTypes = groupedLinks[strategyId];

				// Convert signal types for multi-leg strategies
				if (isMultiLeg) {
					signalTypes = signalTypes.map(type => {
						if (type === 'LE' || type === 'SE') return 'E';
						if (type === 'LX' || type === 'SX') return 'X';
						return type;
					});
				} else {
					signalTypes = signalTypes.map(type => {
						if (type === 'E') return 'LE';
						if (type === 'X') return 'LX';
						return type;
					});
				}

				return { strategyId, signalTypes, multiLeg: isMultiLeg, broker: brokers };
			});
			setStrategyRows(rows);
		} catch (e) {
			console.error('Error in handleEdit:', e);
			toast.error('Failed to load signal data for editing');
		}
	};





	return (
		<div className="quiktrade-algo">
			<div className="algo-content">
				<div className="create-strategy-container">
					{/* Signal Configuration Section */}
					<div className="config-section">
						<div className="qt-algo-section-header">
							<Settings2 size={20} />
							<h3>Signal Configuration</h3>
						</div>
						<div style={{ alignItems: "end" }} className="config-grid">
							<div className="config-item">
								<label>Signal Name</label>
								<Input
									placeholder="e.g. SuperTrend Up"
									value={signalName}
									onChange={(e) => setSignalName(e.target.value)}
									className="signal-name-input"
									size="large"
								/>
							</div>
							{!currentSignalId && (
								<Button
									type="primary"
									loading={loading}
									onClick={saveNameAndGetWebhook}
									className="btn-apply"
								>
									Save Signal
								</Button>
							)}
						</div>
						<div style={{ marginTop: '1rem' }}>

						</div>
					</div>

					{/* Strategy Selection Section */}
					<div className="config-sections-row">
						<div>
							<div className="qt-algo-section-header">
								<AlignHorizontalDistributeCenter size={20} />
								<h3>Strategy Selection</h3>
								<div className="config-grid strategy-select-container">
									<div className="config-item strategy-select-item">
										<label>Add Strategy</label>
										<Select
											key={selectKey}
											showSearch
											placeholder="Select Strategy"
											className="custom-select strategy-select-dropdown"
											onChange={(val) => { addStrategyRow(val); setStrategySelectValue(undefined); }}
											value={strategySelectValue}
											size="large"
											allowClear
											optionFilterProp="children"
											filterOption={(input, option) =>
												(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
											}
										>
											{availableStrategies.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)}
										</Select>
									</div>
								</div>
							</div>
							<Table
								dataSource={strategyRows}
								rowKey={(r) => r.strategyId}
								pagination={false}
								scroll={{ x: true }}
								size="small"
								style={{ marginTop: '1rem' }}
								columns={[
									{ title: 'Strategy', align: 'left', dataIndex: 'strategyId', key: 'strategy', width: '40%', render: (id) => strategies.find(s => s.id === id)?.name || id },
									{
										title: 'Signal Types', align: 'left', dataIndex: 'signalTypes', key: 'types', width: '40%', render: (val, record) => {
											const strategy = strategies.find(s => s.id === record.strategyId);
											const isSingleLeg = strategy?.singleOrMultiLeg === true;
											const availableTypes = isSingleLeg ? singleLegSignalTypes : multiLegSignalTypes;

											return (
												<Select
													mode={isSingleLeg ? "multiple" : undefined}
													value={isSingleLeg ? val : (val?.[0] || undefined)}
													onChange={(v) => {
														const newValue = isSingleLeg ? v : [v];
														setStrategyRows(prev => prev.map(r => r.strategyId === record.strategyId ? { ...r, signalTypes: newValue } : r));
													}}
													style={{ width: '100%' }}
													placeholder="Select signal types"
												>
													{availableTypes.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
												</Select>
											);
										}
									},
									{
										title: 'Actions', align: 'center', key: 'actions', width: '20%', render: (_, record) => (
											<Button danger type="text" onClick={() => removeStrategyRow(record.strategyId)} icon={<Trash2 size={16} />} />
										)
									},
								]}
							/>
							<div className="info-note">
								<ul>
									<li>Signals will be processed in the sequence you see in the signal type field. For example, if you sequence the signal types as Long Exit (LE) , Short Entry (SE). Then first the long exit signal is processed which will close any existing long positions and then Short Entry signal is processed to open a short position.</li>
								</ul>
							</div>
						</div>
						<div>
							<div className="qt-algo-section-header">
								<Link size={20} />
								<h3>Link Brokers</h3>
							</div>
							<Table
								dataSource={strategyRows}
								rowKey={(r) => `br-${r.strategyId}`}
								pagination={false}
								scroll={{ x: false }}
								size="small"
								columns={[
									{ title: 'Strategy', align: 'left', dataIndex: 'strategyId', key: 'strategy', width: '30%', render: (id) => strategies.find(s => s.id === id)?.name || id },
									{
										title: 'Brokers', align: 'left', key: 'broker', width: '35%', render: (_, record) => {
											const currentBrokerIds = (record.broker || []).map(b => b.id);
											return (
												<Select
													mode="multiple"
													placeholder="Select brokers"
													style={{ width: '100%' }}
													value={currentBrokerIds}
													onSelect={(selectedId) => {
														setStrategyRows(prev => prev.map(r => {
															if (r.strategyId === record.strategyId) {
																const existing = (r.broker || []).find(b => b.id === selectedId);
																if (!existing) {
																	const newBrokers = [...(r.broker || []), { id: selectedId, multiplier: 1 }];
																	return { ...r, broker: newBrokers };
																}
															}
															return r;
														}));
													}}
													onChange={(selectedIds) => {
														const currentIds = (record.broker || []).map(b => b.id);
														const isRemoval = selectedIds.length < currentIds.length;

														if (isRemoval) {
															const removedId = currentIds.find(id => !selectedIds.includes(id));
															if (removedId) {
																handleBrokerRemoveConfirm(
																	record.strategyId,
																	removedId,
																	record.broker,
																	() => {
																		setStrategyRows(prev => prev.map(r => {
																			if (r.strategyId === record.strategyId) {
																				const newBrokers = (r.broker || []).filter(b => b.id !== removedId);
																				return { ...r, broker: newBrokers };
																			}
																			return r;
																		}));
																	},
																	() => {
																		// Cancelled - revert by forcing re-render with original value
																		setStrategyRows(prev => prev.map(r => {
																			if (r.strategyId === record.strategyId) {
																				return { ...r };
																			}
																			return r;
																		}));
																	}
																);
																return;
															}
														} else {
															// Adding brokers or other changes - update normally
															setStrategyRows(prev => prev.map(r => {
																if (r.strategyId === record.strategyId) {
																	const newBrokers = selectedIds.map(id => {
																		const existing = (r.broker || []).find(b => b.id === id);
																		return existing || { id, multiplier: 1 };
																	});
																	return { ...r, broker: newBrokers };
																}
																return r;
															}));
														}
													}}
													tagRender={(props) => {
														const { label, value, closable, onClose } = props;
														const brokerLabel = getBrokerLabel(value);
														return (
															<Tag
																className="link-broker-tag"
																closable={closable}
																onClose={(e) => {
																	e.preventDefault();
																	e.stopPropagation();

																	handleBrokerRemoveConfirm(
																		record.strategyId,
																		value,
																		record.broker,
																		() => {
																			setStrategyRows(prev => prev.map(r => {
																				if (r.strategyId === record.strategyId) {
																					const newBrokers = (r.broker || []).filter(b => b.id !== value);
																					return { ...r, broker: newBrokers };
																				}
																				return r;
																			}));
																		},
																	);
																}}
																style={{ marginRight: 3 }}
															>
																{brokerLabel}
															</Tag>
														);
													}}
												>
													{(brokers || []).map((b) => {
														const id = b?.id
														const label = getBrokerLabel(id);
														return (
															<Option key={id} value={id}>{label}</Option>
														);
													})}
												</Select>
											);
										}
									},
									{
										title: 'Multiplier', align: 'center', key: 'multiplier', width: '35%', render: (_, record) => (
											<div className="multiplier-container">
												{(record.broker || []).map((broker) => {
													const label = getBrokerLabel(broker.id);
													return (
														<div key={broker.id} className="multiplier-row">
															<span className="multiplier-label">{label}:</span>
															<PlusMinQty
																filterValue={broker.multiplier}
																qtyClick={(newVal) => updateMultiplier(record.strategyId, broker.id, newVal)}
																min={1}
																step={1}
																name="multiplier"
															/>
														</div>
													);
												})}
											</div>
										)
									},
								]}
							/>
							<div className="signal-footer">
								<Button
									size="large"
									onClick={() => {
										setSignalName("");
										setCurrentSignalId("");
										setStrategyRows([]);
										setSelectKey(k => k + 1);
										toast.success("Form reset successfully");
									}}
									className="btn-reset"
								>
									Reset
								</Button>
								<Button
									type="primary"
									loading={loading}
									onClick={saveStrategyLinks}
									size="large"
									className="btn-apply"
								>
									Save Signal Config
								</Button>
							</div>
						</div>
					</div>

					<div className="config-sections-row">
						<SignalDashboard
							signals={signals}
							strategies={strategies}
							loading={loading}
							handleEdit={handleEdit}
							handleDeactivate={handleDeactivate}
							handleActivate={handleActivate}
							getSignalWebhookUrl={getSignalWebhookUrl}
							handleManualExecution={handleManualExecution}
							handleDeleteConfirm={handleDeleteConfirm}
							getBrokerLabel={getBrokerLabel}
							toast={toast}
						/>

						<CumulativeSignalLogs
							allSignalLogs={allSignalLogs}
							allLogsLoading={allLogsLoading}
							fetchAllSignalLogs={fetchAllSignalLogs}
						/>
					</div>
				</div>
			</div>


			<SubscriptionCheckModal
				open={showSubscriptionModal}
				onClose={() => setShowSubscriptionModal(false)}
				type="Algo"
			/>
		</div>
	);
};

export default SignalsPage;